import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import styles from "../Assessment.module.scss";
import { fetchChallengesAPI } from "@/services/api/Challanges";
import { fetchQuestionsSetAPI, fetchQuestionByIdAPI, fetchAllTagsAPI } from "@/services/api/Question";
import { fetchFreeFormAPI, fetchFreeFormByIdAPI } from "@/services/api/freeForm";
import { createAssessmentAPI, updateAssessmentAPI, fetchAssessmentByIdAPI, fetchAssessmentSettingsAPI, updateAssessmentSettingsAPI, deleteAssessmentAPI, closeAssessmentAPI, fetchAssessmentStatusesAPI } from "@/services/api/assessment";
import EmailTemplateDropdown from "./EmailTemplateDropdown";
import ManagerEmailDropdown from "./ManagerEmailDropdown";
import toast from "react-hot-toast";
import SearchBox from "@/components/SearchBox/SearchBox";
import { DeleteIcon } from "@/assets/svg/Other/DeleteIcon";
import { CloseIcon } from "@/assets/svg/Other/CloseIcon";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { OpenIcon } from "@/assets/svg/Other/OpenIcon";

interface AssessmentEditorProps {
  assessmentId?: number;
  onBack: () => void;
  onSave: (targetTab?: string) => void;
}

const AssessmentEditor: React.FC<AssessmentEditorProps> = ({ assessmentId, onBack, onSave }) => {
  const navigate = useNavigate();
  const { profile } = useSelector((state: RootState) => state.user);
  const currentUserRole = profile?.role?.toUpperCase();
  const isOwnerOrManager = currentUserRole === "OWNER" || currentUserRole === "MANAGER" || currentUserRole === "ADMIN";

  const isEdit = !!(assessmentId && assessmentId > 0);
  const observerTarget = useRef<HTMLDivElement | null>(null);

  // Form State
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [qualifyingPercentage, setQualifyingPercentage] = useState(60);
  const [metadata, setMetadata] = useState<any>(null);
  const [assessment, setAssessment] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [statuses, setStatuses] = useState<{ id: number; name: string; isActive: boolean }[]>([]);

  useEffect(() => {
    const getStatuses = async () => {
      try {
        const res = await fetchAssessmentStatusesAPI();
        const data = res?.data || res;
        if (Array.isArray(data)) {
          setStatuses(data);
        }
      } catch (err) {
        console.error("Failed to load statuses in editor", err);
      }
    };
    getStatuses();
  }, []);

  // Settings Tab State
  const [emailTemplateId, setEmailTemplateId] = useState<number>(0);
  const [timeLimitValue, setTimeLimitValue] = useState<string>("60");
  const [timeLimitUnit, setTimeLimitUnit] = useState<"min" | "hr">("min");
  const [score, setScore] = useState<number | string>(60);
  const [assessmentExpiryDate, setAssessmentExpiryDate] = useState<Dayjs | null>(null);
  const [cheatingDetection, setCheatingDetection] = useState<boolean>(true);
  // emailTemplates state removed — EmailTemplateDropdown fetches its own data with infinite scroll
  const [isPublic, setIsPublic] = useState<boolean>(false);

  // Selection State (Objects)
  const [selectedChallenges, setSelectedChallenges] = useState<any[]>([]);
  const [selectedQuestions, setSelectedQuestions] = useState<any[]>([]);
  const [selectedFreeforms, setSelectedFreeforms] = useState<any[]>([]);

  // Selection Pool & Pagination
  const [challengesPool, setChallengesPool] = useState<any[]>([]);
  const [cPagination, setCPagination] = useState({ pageNo: 0, hasMore: false });

  const [questionsPool, setQuestionsPool] = useState<any[]>([]);
  const [qPagination, setQPagination] = useState({ pageNo: 0, hasMore: false });

  const [freeformsPool, setFreeformsPool] = useState<any[]>([]);
  const [fPagination, setFPagination] = useState({ pageNo: 1, hasMore: false });

  // Filter Data Pools
  const [techTags, setTechTags] = useState<any[]>([]);
  const [jobRoleTags, setJobRoleTags] = useState<any[]>([]);
  const [languageTags, setLanguageTags] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState<"challenges" | "questions" | "freeforms" | "settings">("challenges");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loadingPool, setLoadingPool] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  // Email Edit Modal State
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [selectedEmailId, setSelectedEmailId] = useState<number | string | null>(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Delete Assessment Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const extract = (res: any) => {
    const d = res?.data || res;
    if (Array.isArray(d)) return { list: d, totalPages: 1 };
    const list = d?.results || d?.content || [];
    return { list, totalPages: d?.totalPages || 1 };
  };

  // Set Expansion and Sub-items states
  const [expandedSets, setExpandedSets] = useState<{ [setId: string]: boolean }>({});
  const [setSubItems, setSetSubItems] = useState<{ [setId: string]: any[] }>({});
  const [loadingSubItems, setLoadingSubItems] = useState<{ [setId: string]: boolean }>({});

  const getInitials = (nameStr: string) => {
    if (!nameStr) return "";
    const words = nameStr.trim().split(/\s+/).filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return nameStr.trim().slice(0, 2).toUpperCase();
  };

  const fetchSubItemsForSet = async (setId: number | string, type: "questions" | "freeforms") => {
    if (!setId) return [];
    const stringId = setId.toString();
    if (setSubItems[stringId]) return setSubItems[stringId];

    setLoadingSubItems(prev => ({ ...prev, [stringId]: true }));
    try {
      let items: any[] = [];
      if (type === "questions") {
        const res = await fetchQuestionByIdAPI(stringId);
        const data = res?.data || res;
        items = data?.questions || [];
      } else {
        const res = await fetchFreeFormByIdAPI(stringId);
        const data = res?.data || res;
        items = data?.freeForms || [];
      }
      setSetSubItems(prev => ({ ...prev, [stringId]: items }));
      return items;
    } catch (err) {
      console.error(`Failed to fetch sub-items for ${type} set ${setId}:`, err);
      toast.error("Failed to load nested items");
      return [];
    } finally {
      setLoadingSubItems(prev => ({ ...prev, [stringId]: false }));
    }
  };

  const toggleSetExpand = async (setId: number | string, type: "questions" | "freeforms") => {
    if (!setId) return;
    const stringId = setId.toString();
    const isExpanding = !expandedSets[stringId];
    setExpandedSets(prev => ({ ...prev, [stringId]: isExpanding }));
    if (isExpanding) {
      await fetchSubItemsForSet(setId, type);
    }
  };

  const toggleSetSelection = async (set: any, type: "questions" | "freeforms") => {
    const rawId = set?.id || set?.questionSetId || set?.freeFormSetId || set?.freeformSetId;
    if (!rawId) {
      toast.error("Invalid set: ID not found");
      return;
    }
    const setId = rawId.toString();
    let subItemsList = setSubItems[setId];
    if (!subItemsList) {
      subItemsList = await fetchSubItemsForSet(setId, type);
    }

    if (!subItemsList || subItemsList.length === 0) {
      toast.error("No items in this set to add");
      return;
    }

    const selectedList = type === "questions" ? selectedQuestions : selectedFreeforms;
    const setSelectedList = type === "questions" ? setSelectedQuestions : setSelectedFreeforms;

    const getSubItemId = (sub: any) => sub.id || sub.questionId || sub.freeFormId || sub.freeformId;
    const allSelected = subItemsList.every(sub =>
      selectedList.some(sel => (sel.id || sel.questionId || sel.freeFormId || sel.freeformId) === getSubItemId(sub))
    );

    if (allSelected) {
      const subItemIds = subItemsList.map(sub => getSubItemId(sub));
      const updatedList = selectedList.filter(sel =>
        !subItemIds.includes(sel.id || sel.questionId || sel.freeFormId || sel.freeformId)
      );
      setSelectedList(updatedList);
      toast.success(`Removed set items`);
    } else {
      const toAdd: any[] = [];
      subItemsList.forEach(sub => {
        const subId = getSubItemId(sub);
        const alreadySelected = selectedList.some(sel =>
          (sel.id || sel.questionId || sel.freeFormId || sel.freeformId) === subId
        );
        if (!alreadySelected) {
          const detailedSub = {
            ...sub,
            difficultyLevel: sub.difficultyLevel || sub.difficulty || set.difficultyLevel || set.difficulty || "EASY",
            estimatedTime: sub.estimatedTime || sub.est || undefined,
            technology: sub.technology || set.technology || ""
          };
          toAdd.push(detailedSub);
        }
      });
      setSelectedList([...selectedList, ...toAdd]);
      toast.success(`Added set items`);
    }
  };

  const loadStaticMetadata = useCallback(async () => {
    setLoading(true);
    try {
      const [techRes, roleRes, langRes] = await Promise.all([
        fetchAllTagsAPI(0, 100, "TECHNOLOGY"),
        fetchAllTagsAPI(0, 100, "JOB_ROLE"),
        fetchAllTagsAPI(0, 100, "CODING_LANGUAGE")
      ]);

      setTechTags(extract(techRes).list);
      setJobRoleTags(extract(roleRes).list);
      setLanguageTags(extract(langRes).list);

      // Email templates are now loaded lazily by EmailTemplateDropdown (infinite scroll)
    } catch (err) {
      console.error("Editor load metadata failed", err);
      toast.error("Failed to load editor data");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTabPool = useCallback(async (tab: string, search: string, tagIds: number[] = []) => {
    if (tab === "settings") return;
    setLoadingPool(true);
    console.log(`[AssessmentEditor] loadTabPool called for tab: "${tab}", search: "${search}"`);
    try {
      if (tab === "challenges") {
        console.log("[AssessmentEditor] Calling fetchChallengesAPI...");
        const res = await fetchChallengesAPI(search, "", 0, 10, tagIds);
        const { list } = extract(res);
        console.log("[AssessmentEditor] fetchChallengesAPI returned list:", list);
        setChallengesPool(list);
        setCPagination({ pageNo: 0, hasMore: list.length === 10 });
      } else if (tab === "questions") {
        console.log("[AssessmentEditor] Calling fetchQuestionsSetAPI...");
        const res = await fetchQuestionsSetAPI(0, 10, search, "", tagIds);
        const { list } = extract(res);
        console.log("[AssessmentEditor] fetchQuestionsSetAPI returned list:", list);
        setQuestionsPool(list);
        setQPagination({ pageNo: 0, hasMore: list.length === 10 });
      } else if (tab === "freeforms") {
        console.log("[AssessmentEditor] Calling fetchFreeFormAPI...");
        const res = await fetchFreeFormAPI(1, 10, search);
        const { list } = extract(res);
        console.log("[AssessmentEditor] fetchFreeFormAPI returned list:", list);
        setFreeformsPool(list);
        setFPagination({ pageNo: 1, hasMore: list.length === 10 });
      }
    } catch (err) {
      console.error(`Failed to load ${tab} pool`, err);
      toast.error(`Failed to load ${tab} data`);
    } finally {
      setLoadingPool(false);
    }
  }, []);

  const loadAssessmentDetails = useCallback(async () => {
    if (!assessmentId) return;
    setLoadingDetail(true);
    try {
      const res = await fetchAssessmentByIdAPI(assessmentId);
      console.log("Fetched assessment details:", res);
      const data = res?.data || res;
      if (data) {
        setAssessment(data);
        setName(data.name || "");
        setDescription(data.description || "");
        setQualifyingPercentage(data.qualifyingPercentage || 60);
        setMetadata(data);

        setSelectedChallenges(data.challenges || data.challengeList || []);
        setSelectedQuestions(data.questions || data.questionList || []);
        setSelectedFreeforms(data.freeForms || data.freeforms || data.freeformList || data.freeFormList || data.free_forms || []);
        setIsPublic(data.isPublic ?? false);
      }

      // Load Settings
      try {
        const settingsRes = await fetchAssessmentSettingsAPI(assessmentId);
        const sData = settingsRes?.data || settingsRes;

        if (sData) {
          setEmailTemplateId(sData.emailTemplateId || 0);
          const loadedTimeLimit = String(sData.timeLimit || "60min");
          if (loadedTimeLimit.endsWith("hr")) {
            setTimeLimitValue(loadedTimeLimit.replace("hr", ""));
            setTimeLimitUnit("hr");
          } else if (loadedTimeLimit.endsWith("min")) {
            setTimeLimitValue(loadedTimeLimit.replace("min", ""));
            setTimeLimitUnit("min");
          } else {
            setTimeLimitValue(loadedTimeLimit);
            setTimeLimitUnit("min");
          }
          setScore(sData.score || 60);

          if (sData.assessmentExpiryDate) {
            setAssessmentExpiryDate(dayjs(sData.assessmentExpiryDate));
          }
          setCheatingDetection(sData.cheatingDetection ?? true);
        }
      } catch (err) {
        console.error("Failed to load assessment settings", err);
      }
    } catch (err) {
      console.error("Failed to fetch assessment details", err);
    } finally {
      setLoadingDetail(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadStaticMetadata();
  }, [loadStaticMetadata]);

  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return;
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    loadTabPool(activeTab, debouncedSearchTerm, selectedTagIds);
  }, [activeTab, debouncedSearchTerm, selectedTagIds, loadTabPool]);

  const handleTabChange = (tab: "challenges" | "questions" | "freeforms" | "settings") => {
    setActiveTab(tab);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedTagIds([]);

    // Clear pools to avoid stale data showing during tab transitions
    setChallengesPool([]);
    setQuestionsPool([]);
    setFreeformsPool([]);

    // Reset pagination
    setCPagination({ pageNo: 0, hasMore: false });
    setQPagination({ pageNo: 0, hasMore: false });
    setFPagination({ pageNo: 1, hasMore: false });
  };

  useEffect(() => {
    if (isEdit) {
      loadAssessmentDetails();
    }
  }, [isEdit, loadAssessmentDetails]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || loading || loadingPool) return;
    const hasMore = activeTab === "challenges" ? cPagination.hasMore : (activeTab === "questions" ? qPagination.hasMore : fPagination.hasMore);
    if (!hasMore) return;

    setLoadingMore(true);
    try {
      if (activeTab === "challenges") {
        const nextPage = cPagination.pageNo + 1;
        const res = await fetchChallengesAPI(searchTerm, "", nextPage, 10, selectedTagIds);
        const { list } = extract(res);
        if (list.length > 0) {
          setChallengesPool(prev => [...prev, ...list]);
          setCPagination({ pageNo: nextPage, hasMore: list.length === 10 });
        } else setCPagination(prev => ({ ...prev, hasMore: false }));
      } else if (activeTab === "questions") {
        const nextPage = qPagination.pageNo + 1;
        const res = await fetchQuestionsSetAPI(nextPage, 10, searchTerm, "", selectedTagIds);
        const { list } = extract(res);
        if (list.length > 0) {
          setQuestionsPool(prev => [...prev, ...list]);
          setQPagination({ pageNo: nextPage, hasMore: list.length === 10 });
        } else setQPagination(prev => ({ ...prev, hasMore: false }));
      } else if (activeTab === "freeforms") {
        const nextPage = fPagination.pageNo + 1;
        const res = await fetchFreeFormAPI(nextPage, 10, searchTerm);
        const { list } = extract(res);
        if (list.length > 0) {
          setFreeformsPool(prev => [...prev, ...list]);
          setFPagination({ pageNo: nextPage, hasMore: list.length === 10 });
        } else setFPagination(prev => ({ ...prev, hasMore: false }));
      }
    } catch (err) { console.error(err); } finally { setLoadingMore(false); }
  }, [activeTab, cPagination, qPagination, fPagination, loadingMore, loading, loadingPool, searchTerm, selectedTagIds]);

  useEffect(() => {
    const observer = new IntersectionObserver(entries => { if (entries[0]?.isIntersecting) handleLoadMore(); }, { threshold: 0.1 });
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => { if (observerTarget.current) observer.unobserve(observerTarget.current); };
  }, [handleLoadMore]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Assessment name is required");
      return;
    }
    const payload = {
      name,
      description,
      qualifyingPercentage: score === "" ? 0 : Number(score), // Sync passing score with assessment qualifying percentage
      challengeIds: selectedChallenges.map(c => c.id || c.challengeId || (c as any).challenge_id),
      questionIds: selectedQuestions.map(q => q.id || q.questionId || (q as any).question_id),
      freeFormsIds: selectedFreeforms.map(f => f.id || f.freeFormId || f.freeformId),
      isPublic: isPublic,
      isActive: true
    };
    setSaving(true);
    try {
      let savedId = assessmentId;
      if (isEdit) {
        await updateAssessmentAPI(assessmentId!, payload);
      } else {
        const res = await createAssessmentAPI(payload);
        const created = res?.data || res;
        savedId = created?.id || created?.assessmentId;
      }

      // Now save the assessment settings!
      if (savedId) {
        const settingsPayload = {
          assessmentId: savedId,
          emailTemplateId: emailTemplateId || null,
          timeLimit: `${timeLimitValue || "60"}${timeLimitUnit}`,
          score: score === "" ? 0 : Number(score),
          assessmentExpiryDate: assessmentExpiryDate ? assessmentExpiryDate.format("YYYY-MM-DD HH:mm:ss") : null,
          cheatingDetection: cheatingDetection
        };
        await updateAssessmentSettingsAPI(savedId, settingsPayload);
      }

      toast.success(isEdit ? "Assessment updated successfully!" : "Assessment created successfully!");
      // if (onSave) onSave();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save assessment");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    const payload = {
      name: `${name} copy`,
      description,
      qualifyingPercentage: score === "" ? 0 : Number(score),
      challengeIds: selectedChallenges.map(c => c.id || c.challengeId || (c as any).challenge_id),
      questionIds: selectedQuestions.map(q => q.id || q.questionId || (q as any).question_id),
      freeFormsIds: selectedFreeforms.map(f => f.id || f.freeFormId || f.freeformId),
      isPublic: isPublic,
      isActive: true
    };
    
    setSaving(true);
    try {
      const res = await createAssessmentAPI(payload);
      const created = res?.data || res;
      const savedId = created?.id || created?.assessmentId;

      if (savedId) {
        const settingsPayload = {
          assessmentId: savedId,
          emailTemplateId: emailTemplateId || null,
          timeLimit: `${timeLimitValue || "60"}${timeLimitUnit}`,
          score: score === "" ? 0 : Number(score),
          assessmentExpiryDate: assessmentExpiryDate ? assessmentExpiryDate.format("YYYY-MM-DD HH:mm:ss") : null,
          cheatingDetection: cheatingDetection
        };
        await updateAssessmentSettingsAPI(savedId, settingsPayload);
      }
      toast.success("Assessment duplicated successfully!");
      setIsDuplicateModalOpen(false);
      if (onSave) onSave();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to duplicate assessment");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEmailModal = () => {
    setEditEmail(assessment?.email || "");
    setSelectedEmailId(null);
    setEmailError("");
    setIsEmailModalOpen(true);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentId) return;
    if (!isOwnerOrManager) {
      toast.error("You do not have permission to delete assessments.");
      return;
    }
    setDeleting(true);
    try {
      const res = await deleteAssessmentAPI(assessmentId);
      const msg = res?.message || res?.data?.message || "Assessment deleted successfully!";
      toast.success(msg);
      setIsDeleteModalOpen(false);
      onSave(); // Navigate back to list
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message || "Failed to delete assessment";
      toast.error(errMsg);
    } finally {
      setDeleting(false);
    }
  };

  const closedStatusId = statuses.find(s => s.name === "CLOSED")?.id || 2;
  const openStatusId = statuses.find(s => s.name === "OPEN")?.id || 1;
  const currentStatusId = assessment?.assessmentStatusId || assessment?.assessmentStatus?.id;

  const handleToggleStatus = async () => {
    if (!assessmentId) return;
    setUpdatingStatus(true);
    const newStatusId = currentStatusId === closedStatusId ? openStatusId : closedStatusId;
    try {
      await closeAssessmentAPI(assessmentId, newStatusId);
      toast.success(newStatusId === closedStatusId ? "Assessment temporarily closed!" : "Assessment activated successfully!");
      setIsCloseModalOpen(false);
      if (newStatusId === closedStatusId) {
        onSave("1"); // Navigate back to closed list page when closed
      } else {
        onSave("0"); // Navigate back to open list page when activated
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.message || "Failed to update assessment status";
      toast.error(errMsg);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleToggleStatusClick = () => {
    const isCurrentlyClosed = currentStatusId === closedStatusId;
    if (isCurrentlyClosed) {
      handleToggleStatus();
    } else {
      setIsCloseModalOpen(true);
    }
  };

  const handleSaveEmail = async () => {
    if (!editEmail || !/^\S+@\S+\.\S+$/.test(editEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (!assessmentId) return;

    setSavingEmail(true);
    try {
      const payload: any = { email: editEmail };
      if (selectedEmailId) {
        payload.managedBy = selectedEmailId;
      }
      await updateAssessmentAPI(assessmentId, payload);
      setAssessment((prev: any) => ({ ...prev, email: editEmail }));
      toast.success("Email updated successfully!");
      setIsEmailModalOpen(false);
    } catch (err: any) {
      // toast.error(err?.response?.data?.message || "Failed to update email");
    } finally {
      setSavingEmail(false);
    }
  };

  const toggleItem = (item: any, selectedList: any[], setSelectedList: (v: any[]) => void) => {
    const id = item.id || item.challengeId || item.questionId || item.freeFormId;
    const isSelected = selectedList.some(i => (i.id || i.challengeId || i.questionId || i.freeFormId) === id);
    if (isSelected) {
      setSelectedList(selectedList.filter(i => (i.id || i.challengeId || i.questionId || i.freeFormId) !== id));
    } else {
      setSelectedList([...selectedList, item]);
    }
  };

  const renderSelectedItem = (item: any, list: any[], setter: (v: any[]) => void) => {
    const id = item.id || item.challengeId || item.questionId || item.freeFormId;
    const diff = item.difficultyLevel || item.difficulty || 'Easy';
    const est = item.estimatedTime || item.est;
    const estVal = est ? (typeof est === 'number' ? `${est} min` : (String(est).toLowerCase().includes('min') ? String(est) : `${String(est)} min`)) : "";

    return (
      <div key={id} className={styles.selectedItemBox}>
        <h5 dangerouslySetInnerHTML={{ __html: item.title || item.name }}></h5>
        <div className={styles.itemTagsRow}>
          <span className={`${styles.badge} ${styles[diff] || styles[diff.toLowerCase()] || styles[diff.toUpperCase()] || diff}`}>{diff}</span>
          {estVal && <span className={styles.tag}>Est: {estVal}</span>}
          <button className={styles.delete} onClick={() => toggleItem(item, list, setter)} title="Remove">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><line x1="10" y1="11" x2="10" y2="17" /><line x1="14" y1="11" x2="14" y2="17" /></svg>
          </button>
        </div>
      </div>
    );
  };

  const formatDate = (dateStr: any) => {
    if (!dateStr) return "Today";
    // If it's already a string from @JsonFormat, we'll try to use it directly
    // but usually new Date() handles MMM dd yyyy okay
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr; // Return as is if date parsing fails
    return d.toLocaleDateString();
  };

  const currentPool = activeTab === "challenges" ? challengesPool : (activeTab === "questions" ? questionsPool : freeformsPool);

  if (loading) return <div className={styles.loaderContainer}><div className={styles.loader}></div></div>;

  return (
    <div className={styles.premiumEditor}>
      <div className={styles.topBar}>
        <span className={styles.backLink} onClick={onBack}>&lt; Back to assessments</span>
        <div className={styles.headerMain}>
          <div className={styles.titleArea}>
            <input className={styles.editorTitleInput} placeholder="Enter assessment name" value={name} onChange={e => setName(e.target.value)} style={{ background: 'transparent', border: 'none', fontSize: '28px', fontWeight: '700', color: 'var(--text)', outline: 'none', padding: 0 }} />
            <div className={styles.metaInfo}>
              <span >Managed by <strong>{assessment?.email || (isEdit ? "Loading..." : "you")}</strong></span>
              <span style={{ marginLeft: '6px', display: 'flex', alignItems: 'center' }}>
                Created on {formatDate(assessment?.createdAt)}
                {isEdit && (
                  <button
                    onClick={handleOpenEmailModal}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', marginLeft: '8px', color: '#7cccb1', padding: '0', display: 'flex', alignItems: 'center' }}
                    title="Edit Email"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
                  </button>
                )}
              </span>
            </div>
          </div>
          <div className={styles.headerActions}>
            {isEdit && (
              <button className={styles.btnGhost} onClick={() => setIsDuplicateModalOpen(true)} disabled={saving}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="13" height="13" x="9" y="9" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>Duplicate</button>
            )}
            <button className={styles.btnGhost} onClick={() => {
              if (isEdit) {
                localStorage.removeItem(`assessment_end_time_preview_${assessmentId}`);
                localStorage.removeItem(`assessment_duration_preview_${assessmentId}`);
                navigate(`/assessment-preview/${assessmentId}`);
              } else {
                toast.error("Please save the assessment first to preview");
              }
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
              Preview</button>
            <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
          </div>
        </div>
      </div>

      <div className={styles.editorTabs}>
        <div className={`${styles.editorTab} ${activeTab === "challenges" ? styles.activeTab : ''}`} onClick={() => handleTabChange("challenges")}>CHALLENGES ({selectedChallenges.length})</div>
        <div className={`${styles.editorTab} ${activeTab === "questions" ? styles.activeTab : ''}`} onClick={() => handleTabChange("questions")}>QUESTIONS ({selectedQuestions.length})</div>
        <div className={`${styles.editorTab} ${activeTab === "freeforms" ? styles.activeTab : ''}`} onClick={() => handleTabChange("freeforms")}>FREE-FORM ({selectedFreeforms.length})</div>
        <div className={`${styles.editorTab} ${activeTab === "settings" ? styles.activeTab : ''}`} onClick={() => handleTabChange("settings")}>SETTINGS</div>
      </div>

      <div className={styles.editorContent}>
        {activeTab === "settings" ? (
          <main className={styles.settingsFormContainer}>
            <div className={styles.settingsFormCard}>
              <h3 className={styles.settingsFormTitle}>Assessment Settings</h3>
              <p className={styles.settingsFormSubtitle}>Configure time limits, passing requirements, security features, and invitation email templates.</p>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Time Limit</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="number"
                      value={timeLimitValue}
                      onChange={e => setTimeLimitValue(e.target.value)}
                      placeholder="e.g. 60"
                      style={{ flex: 1 }}
                      min="1"
                    />
                    <select
                      value={timeLimitUnit}
                      onChange={e => setTimeLimitUnit(e.target.value as "min" | "hr")}
                      style={{
                        padding: '12px 16px',
                        border: '1.5px solid var(--border)',
                        borderRadius: '8px',
                        background: 'var(--surface)',
                        color: 'var(--text)',
                        fontSize: '14px',
                        outline: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="min">min</option>
                      <option value="hr">hr</option>
                    </select>
                  </div>
                  <span className={styles.inputHelp}>Candidates must finish the assessment within this duration once started.</span>
                </div>

                <div className={styles.formGroup}>
                  <label>Passing Score (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={score}
                    onChange={e => {
                      const val = e.target.value.replace(/^0+(?!$)/, "");
                      setScore(val);
                    }}
                    placeholder="e.g. 60"
                  />
                  <span className={styles.inputHelp}>Minimum percentage required to pass the evaluation.</span>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Assessment Expiry Date</label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      value={assessmentExpiryDate}
                      onChange={(newValue) => setAssessmentExpiryDate(newValue)}
                      format="YYYY-MM-DD hh:mm A"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiInputBase-root, & .MuiPickersInputBase-root': {
                              color: 'var(--text, #ffffff) !important',
                              background: 'var(--surface-2, #111827)',
                              borderRadius: '8px',
                              height: '44px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderWidth: '1px',
                              borderColor: 'var(--border, rgba(255, 255, 255, 0.15))',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--accent, #3b82f6)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--accent, #3b82f6)',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--text-2, #94a3b8) !important',
                            },
                            '& .MuiInputBase-input::placeholder': {
                              color: 'var(--text-3, rgba(255, 255, 255, 0.4)) !important',
                              opacity: 1,
                            },
                            '& .MuiInputBase-input, & .MuiPickersSectionList-root, & .MuiPickersSectionList-root *': {
                              color: 'var(--text, #ffffff) !important',
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                  <span className={styles.inputHelp}>The date and 12-hr time after which candidates can no longer start this assessment.</span>
                </div>

                {isOwnerOrManager && (
                  <div className={styles.formGroup}>
                    <label>Invitation Email Template</label>
                    <EmailTemplateDropdown
                      value={emailTemplateId}
                      onChange={(id) => setEmailTemplateId(id)}
                    />
                    <span className={styles.inputHelp}>Select the custom email template sent to invited candidates. The template ID is saved in settings.</span>
                  </div>
                )}

              </div>

              <div className={styles.formGroup} style={{ marginTop: '16px' }}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={cheatingDetection}
                    onChange={e => setCheatingDetection(e.target.checked)}
                  />
                  <span className={styles.checkboxCustom}></span>
                  <div className={styles.checkboxTextGroup}>
                    <strong>Enable Cheating Detection</strong>
                    <span className={styles.checkboxSubtext}>Actively monitors screen switches, copy-pasting, and triggers security violations.</span>
                  </div>
                </label>
              </div>

              <div className={styles.formGroup} style={{ marginTop: '16px' }}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={e => setIsPublic(e.target.checked)}
                  />
                  <span className={styles.checkboxCustom}></span>
                  <div className={styles.checkboxTextGroup}>
                    <strong>Enable Public Link Access</strong>
                    <span className={styles.checkboxSubtext}>Allow anyone with the public link to self-register and take the assessment.</span>
                  </div>
                </label>
              </div>
              {/* Danger Zone - Status & Delete */}
              {isEdit && (
                <>
                  {/* Temporarily Close / Activate Assessment */}
                  <div className={currentStatusId === closedStatusId ? styles.primaryZoneCard : styles.warningZoneCard}>
                    <div className={styles.dangerZoneLeft}>
                      <div className={currentStatusId === closedStatusId ? styles.primaryZoneIcon : styles.warningZoneIcon}>
                        {currentStatusId === closedStatusId ? <OpenIcon /> : <CloseIcon />}
                      </div>
                      <div>
                        <span className={currentStatusId === closedStatusId ? styles.primaryZoneTitle : styles.warningZoneTitle}>
                          {currentStatusId === closedStatusId ? "Open Assessment" : "Temporarily Close Assessment"}
                        </span>
                        <span className={currentStatusId === closedStatusId ? styles.primaryZoneSubtext : styles.warningZoneSubtext}>
                          {currentStatusId === closedStatusId
                            ? "Reopen this assessment to allow candidates to take it."
                            : "Temporarily pause the assessment. Candidates will not be able to access it."}
                        </span>
                      </div>
                    </div>
                    <button
                      id="toggle-status-assessment-btn"
                      className={currentStatusId === closedStatusId ? styles.primaryZoneBtn : styles.warningZoneBtn}
                      onClick={handleToggleStatusClick}
                      disabled={updatingStatus}
                    >
                      {updatingStatus
                        ? "Processing..."
                        : (currentStatusId === closedStatusId ? "Open Assessment" : "Close Assessment")}
                    </button>
                  </div>

                  {/* Delete Assessment */}
                  {isOwnerOrManager && (
                    <div className={styles.dangerZoneCard}>
                      <div className={styles.dangerZoneLeft}>
                        <div className={styles.dangerZoneIcon}>
                          <DeleteIcon />
                        </div>
                        <div>
                          <span className={styles.dangerZoneTitle}>Delete Assessment</span>
                          <span className={styles.dangerZoneSubtext}>Permanently remove this assessment and all related data.</span>
                        </div>
                      </div>
                      <button
                        id="delete-assessment-btn"
                        className={styles.dangerZoneBtn}
                        onClick={() => setIsDeleteModalOpen(true)}
                      >
                        Delete Assessment
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        ) : (
          <>
            <aside className={styles.filterSidebar}>
              <h3>Filters</h3>
              <SearchBox
                value={searchTerm}
                onChange={(val) => setSearchTerm(val)}
                // placeholder="Search library..."
                width="200px"
              />
              <div style={{ marginTop: '1.5rem' }} />
              <div className={styles.filterGroup}>
                <h4>Technology</h4>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {techTags.map(tag => (
                    <label key={tag.id} className={styles.filterItem}>
                      <input
                        type="checkbox"
                        checked={selectedTagIds.includes(tag.id)}
                        onChange={() => handleTagToggle(tag.id)}
                      />
                      <span className={styles.filterCheckboxCustom} />
                      {tag.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.filterGroup}>
                <h4>Job Role</h4>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {jobRoleTags.map(tag => (
                    <label key={tag.id} className={styles.filterItem}>
                      <input
                        type="checkbox"
                        checked={selectedTagIds.includes(tag.id)}
                        onChange={() => handleTagToggle(tag.id)}
                      />
                      <span className={styles.filterCheckboxCustom} />
                      {tag.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.filterGroup}>
                <h4>Language</h4>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {languageTags.map(tag => (
                    <label key={tag.id} className={styles.filterItem}>
                      <input
                        type="checkbox"
                        checked={selectedTagIds.includes(tag.id)}
                        onChange={() => handleTagToggle(tag.id)}
                      />
                      <span className={styles.filterCheckboxCustom} />
                      {tag.name}
                    </label>
                  ))}
                </div>
              </div>
            </aside>

            <main className={styles.libraryContainer}>
              <div className={styles.libraryHeader}><h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} library</h2></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {loadingPool ? (
                  <div className={styles.loaderContainer} style={{ padding: '2rem 0', display: 'flex', justifyContent: 'center' }}>
                    <div className={styles.loader}></div>
                  </div>
                ) : Array.isArray(currentPool) && currentPool.length > 0 ? (
                  currentPool.map(item => {
                    const id = item.id || item.challengeId || item.questionId || item.freeFormId || item.questionSetId || item.freeFormSetId || item.freeformSetId;
                    const stringId = id.toString();

                    if (activeTab === "challenges") {
                      const isSelected = selectedChallenges.some(i => (i.id || i.challengeId) === id);
                      return (
                        <div key={id} className={styles.libraryCard}>
                          <div className={styles.cardTop}>
                            <div className={styles.titleLine}>
                              <h4 dangerouslySetInnerHTML={{ __html: item.title || item.name }}></h4>
                              {item.difficultyLevel && (
                                <span className={`${styles.badge} ${styles[item.difficultyLevel] || item.difficultyLevel}`}>
                                  {item.difficultyLevel}
                                </span>
                              )}
                              {(() => {
                                const est = item.estimatedTime || item.est;
                                if (!est) return null;
                                const estVal = typeof est === 'number' ? `${est} min` : (String(est).toLowerCase().includes('min') ? String(est) : `${String(est)} min`);
                                return <span className={styles.estTime}>Est: {estVal}</span>;
                              })()}
                            </div>
                          </div>
                          <div className={styles.cardDesc} dangerouslySetInnerHTML={{ __html: item.shortDescription || "This is a private content item linked to your library." }}></div>
                          <div className={styles.cardFooter}>
                            <div className={styles.tech}>{item.technology && <span>{item.technology}</span>} {item.type && <span>{item.type}</span>}</div>
                            <div className={styles.cardBtns}>
                              <button className={`${styles.addBtn} ${isSelected ? styles.selected : ""}`} onClick={() => toggleItem(item, selectedChallenges, setSelectedChallenges)}>{isSelected ? "−" : "+"}</button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // For Questions and Free-forms Sets Accordion Render
                    const isExpanded = !!expandedSets[stringId];
                    const isLoadingSub = !!loadingSubItems[stringId];
                    const subItemsList = setSubItems[stringId] || [];

                    // Check if all subitems are selected
                    const selectedList = activeTab === "questions" ? selectedQuestions : selectedFreeforms;
                    const setSelectedList = activeTab === "questions" ? setSelectedQuestions : setSelectedFreeforms;

                    const getSubItemId = (sub: any) => sub.id || sub.questionId || sub.freeFormId || sub.freeformId;
                    const subItemsSelectedCount = subItemsList.filter(sub =>
                      selectedList.some(sel => (sel.id || sel.questionId || sel.freeFormId || sel.freeformId) === getSubItemId(sub))
                    ).length;

                    const isSetAllSelected = subItemsList.length > 0 && subItemsSelectedCount === subItemsList.length;

                    return (
                      <div key={id} className={`${styles.setCard} ${isExpanded ? styles.expanded : ""}`}>
                        {/* Set Header */}
                        <div className={styles.setHeader} onClick={() => toggleSetExpand(id, activeTab)}>
                          <div className={styles.setHeaderLeft}>
                            <div className={styles.setInitials}>
                              {getInitials(item.name || item.title || "")}
                            </div>
                            <div className={styles.setHeaderMeta}>
                              <div className={styles.setNameRow}>
                                <h3>{item.name || item.title}</h3>
                                {item.difficultyLevel && <span className={`${styles.badge} ${styles[item.difficultyLevel] || item.difficultyLevel}`}>{item.difficultyLevel}</span>}
                              </div>
                              {/* <p className={styles.setDescription}>
                                {item.description || "Contains library items."}
                              </p> */}
                            </div>
                          </div>
                          <div className={styles.setHeaderRight} onClick={(e) => e.stopPropagation()}>
                            <button
                              className={`${styles.setAddBtn} ${isSetAllSelected ? styles.allSelected : ""}`}
                              onClick={() => toggleSetSelection(item, activeTab)}
                              title={isSetAllSelected ? "Remove all from selection" : "Add all to selection"}
                            >
                              {isSetAllSelected ? "−" : "+"}
                            </button>
                            <button
                              className={`${styles.expandBtn} ${isExpanded ? styles.expanded : ""}`}
                              onClick={() => toggleSetExpand(id, activeTab)}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="6 9 12 15 18 9"></polyline>
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Nested Sub-items list */}
                        {isExpanded && (
                          <div className={styles.setSubItemsList}>
                            {isLoadingSub ? (
                              <div className={styles.loaderContainer} style={{ padding: '1rem 0', display: 'flex', justifyContent: 'center' }}>
                                <div className={styles.loader} style={{ width: '20px', height: '20px' }}></div>
                              </div>
                            ) : subItemsList.length > 0 ? (
                              subItemsList.map(subItem => {
                                const subId = getSubItemId(subItem);
                                const isSubSelected = selectedList.some(sel => (sel.id || sel.questionId || sel.freeFormId || sel.freeformId) === subId);

                                const subDiff = subItem.difficultyLevel || subItem.difficulty || item.difficultyLevel || "EASY";
                                const subEst = subItem.estimatedTime || subItem.est;
                                const subEstVal = subEst ? (typeof subEst === 'number' ? `${subEst} min` : (String(subEst).toLowerCase().includes('min') ? String(subEst) : `${String(subEst)} min`)) : "";

                                return (
                                  <div key={subId} className={styles.subItemCard}>
                                    <div className={styles.subItemLeft}>
                                      <h4 className={styles.subItemTitle} dangerouslySetInnerHTML={{ __html: subItem.title || subItem.name || subItem.question || subItem.description }}></h4>
                                      <div className={styles.subItemMeta}>
                                        <span className={`${styles.badge} ${styles[subDiff] || subDiff}`}>{subDiff}</span>
                                        {activeTab === "questions" && (() => {
                                          const rawType = subItem.type || (subItem.options && subItem.options.length > 0 ? "MULTIPLE_CHOICE" : "SHORT_ANSWER");
                                          const typeLabel = rawType === "MULTIPLE_CHOICE" ? "Multiple Choice" : "Short Answer";
                                          return <span className={`${styles.badge} ${styles[rawType] || rawType}`}>{typeLabel}</span>;
                                        })()}
                                        {subEstVal && <span className={styles.estTime}>Est: {subEstVal}</span>}
                                      </div>
                                    </div>
                                    <div className={styles.subItemRight}>
                                      <button
                                        className={`${styles.subAddBtn} ${isSubSelected ? styles.selected : ""}`}
                                        onClick={() => toggleItem({
                                          ...subItem,
                                          difficultyLevel: subDiff,
                                          estimatedTime: subEstVal || undefined,
                                          technology: subItem.technology || item.technology || ""
                                        }, selectedList, setSelectedList)}
                                      >
                                        {isSubSelected ? "−" : "+"}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ textAlign: 'center', color: '#64748b', padding: '1rem', fontSize: '13px' }}>
                                No items in this set
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', color: '#64748b', padding: '2rem' }}>
                    No {activeTab} found
                  </div>
                )}
                {!loadingPool && (
                  <div ref={observerTarget} style={{ height: '20px' }}>
                    {loadingMore && <div className={styles.loader} style={{ margin: '0 auto', width: '24px', height: '24px' }}></div>}
                  </div>
                )}
              </div>
            </main>

            <aside className={styles.selectedSidebar}>
              <div className={styles.sidebarHeader}><h3 style={{ fontSize: '15px' }}>Summary of Selection</h3></div>
              <div className={styles.selectedStack}>
                <div style={{ marginBottom: '2rem' }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', }}><span style={{ fontWeight: '700', fontSize: '12px', color: '#64748b' }}>CHALLENGES ({selectedChallenges.length})</span></div>{selectedChallenges.map(item => renderSelectedItem(item, selectedChallenges, setSelectedChallenges))}{selectedChallenges.length === 0 && <span style={{ fontSize: '12px', fontStyle: 'italic' }}>None selected</span>}</div>
                <div style={{ marginBottom: '2rem' }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', }}><span style={{ fontWeight: '700', fontSize: '12px', color: '#64748b' }}>QUESTIONS ({selectedQuestions.length})</span></div>{selectedQuestions.map(item => renderSelectedItem(item, selectedQuestions, setSelectedQuestions))}{selectedQuestions.length === 0 && <span style={{ fontSize: '12px', fontStyle: 'italic' }}>None selected</span>}</div>
                <div style={{ marginBottom: '2rem' }}><div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', }}><span style={{ fontWeight: '700', fontSize: '12px', color: '#64748b' }}>FREE-FORM ({selectedFreeforms.length})</span></div>{selectedFreeforms.map(item => renderSelectedItem(item, selectedFreeforms, setSelectedFreeforms))}{selectedFreeforms.length === 0 && <span style={{ fontSize: '12px', fontStyle: 'italic' }}>None selected</span>}</div>
              </div>
            </aside>
          </>
        )}
      </div>

      {isEmailModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.emailModal}>
            <h3 className={styles.emailModalTitle}>Edit Manager Email</h3>
            <div className={styles.emailModalField}>
              <label className={styles.emailModalLabel}>Email Address</label>
              <ManagerEmailDropdown
                value={editEmail}
                onChange={(email, id) => {
                  setEditEmail(email);
                  setSelectedEmailId(id);
                  setEmailError("");
                }}
              />
              {emailError && <p className={styles.emailModalError}>{emailError}</p>}
            </div>
            <div className={styles.emailModalActions}>
              <button
                onClick={() => setIsEmailModalOpen(false)}
                className={styles.deleteModalCancelBtn}
                disabled={savingEmail}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEmail}
                className={styles.emailModalSaveBtn}
                disabled={savingEmail}
              >
                {savingEmail ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Assessment Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModal}>
            <div className={styles.deleteModalIconWrap}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <h3 className={styles.deleteModalTitle}>Delete Assessment?</h3>
            <p className={styles.deleteModalMsg}>
              Are you sure you want to <strong>permanently delete</strong> this assessment?
            </p>
            <div className={styles.deleteModalActions}>
              <button
                id="cancel-delete-assessment-btn"
                className={styles.deleteModalCancelBtn}
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                id="confirm-delete-assessment-btn"
                className={styles.deleteModalConfirmBtn}
                onClick={handleDeleteAssessment}
                disabled={deleting}
              >
                {deleting ? (
                  <><span className={styles.deletingSpinner}></span> Deleting...</>
                ) : (
                  <>Delete Assessment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Close Assessment Confirmation Modal */}
      {isCloseModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModal}>
            <div className={styles.warningZoneIcon} style={{ margin: '0 auto 16px auto', width: '56px', height: '56px', borderRadius: '50%' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <line x1="9" y1="9" x2="15" y2="15" />
                <line x1="15" y1="9" x2="9" y2="15" />
              </svg>
            </div>
            <h3 className={styles.deleteModalTitle} style={{ color: 'var(--medium)' }}>Close Assessment?</h3>
            <p className={styles.deleteModalMsg}>
              Are you sure you want to <strong>temporarily close</strong> this assessment? Candidates will no longer be able to access or take it.
            </p>
            <div className={styles.deleteModalActions}>
              <button
                id="cancel-close-assessment-btn"
                className={styles.deleteModalCancelBtn}
                onClick={() => setIsCloseModalOpen(false)}
                disabled={updatingStatus}
              >
                Cancel
              </button>
              <button
                id="confirm-close-assessment-btn"
                className={styles.warningZoneBtn}
                style={{ padding: '8px 16px', borderRadius: '6px' }}
                onClick={handleToggleStatus}
                disabled={updatingStatus}
              >
                {updatingStatus ? "Closing..." : "Close Assessment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Assessment Confirmation Modal */}
      {isDuplicateModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.deleteModal}>
            <div className={styles.deleteModalIconWrap} style={{ background: 'var(--accent-dim)', color: 'var(--accent)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="13" height="13" x="9" y="9" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            </div>
            <h3 className={styles.CopyModalTitle}>Are you sure you want to duplicate this assessment?</h3>
            <p className={styles.deleteModalMsg}>
              A new assessment will be created with all question sets, coding challenges, SQL challenges, and free-form questions copied from the current assessment.
            </p>
            <div className={styles.deleteModalActions}>
              <button
                id="cancel-duplicate-assessment-btn"
                className={styles.deleteModalCancelBtn}
                onClick={() => setIsDuplicateModalOpen(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                id="confirm-duplicate-assessment-btn"
                className={styles.duplicateModalConfirmBtn}
                onClick={handleDuplicate}
                disabled={saving}
              >
                {saving ? "Duplicating..." : "Duplicate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentEditor;
